import { AxiosProgressEvent } from 'axios';
import { findIndex, isEmpty, omit } from 'lodash';
import { StateCreator } from 'zustand';

import { QuoteType, RegUser, RegUserMigration } from '@common/types';
import {
  HEALTH_FAST_QUOTE_STEPS,
  HEALTH_QUOTE_STEPS,
  OPTIMA_RESTORE,
  OPTIMA_SECURE
} from '@healthquote/constants';
import {
  getHealthPremium,
  getMembersWithAgeAndMedConditions
} from '@healthquote/utils';
import insureAPIs, {
  finalizeHealthQuoteTransform,
  finalizeMigrationHealthQuoteTransform,
  healthEstimateTransform,
  healthPremiumMigrationTransform,
  HealthPremiumResponse,
  healthPremiumTransform,
  healthSuggestionTransform
} from '@services/apis/insure';
import { SessionStorage } from '@services/storage/storage';
import { isUnlimitedSI } from '@utils';
import { HEALTH_INSURANCE_PRODUCTS } from '@utils/constants';

import { getHealthProductDetails, getStepProgress } from './selectors';
import {
  ComboType,
  HealthAddOn,
  HealthAddOnPremiums,
  HealthAddOnSubOptionValue,
  HealthAddOns,
  HealthEMIFrequency,
  HealthMember,
  HealthPremium,
  HealthQuoteMember,
  HealthQuoteStoreState,
  SuggestedHealthProduct,
  Suggestion,
  SumInsuredValue,
  UserSliceState
} from './types';

// Utility function to sort members based on HealthQuoteMember enum order
const sortMembersByRelation = (members: HealthMember[]): HealthMember[] => {
  const memberOrder = [
    HealthQuoteMember.Self,
    HealthQuoteMember.Spouse,
    HealthQuoteMember.Father,
    HealthQuoteMember.Mother,
    HealthQuoteMember.FatherInLaw,
    HealthQuoteMember.MotherInLaw,
    HealthQuoteMember.Son,
    HealthQuoteMember.Daughter
  ];

  return members.sort((a, b) => {
    const aIndex = memberOrder.indexOf(a.relation);
    const bIndex = memberOrder.indexOf(b.relation);
    return aIndex - bIndex;
  });
};

const planInitialValues = {
  members: [],
  draftMembers: [],
  pincode: undefined,
  sumInsured: 0,
  product: '',
  ped: [],
  tenure: 1,
  optedEmi: false
};

export const userSlice: StateCreator<
  HealthQuoteStoreState,
  [['zustand/immer', never], ['zustand/devtools', never]],
  [],
  UserSliceState
> = (set, get) => ({
  quote: QuoteType.Normal,
  plan: planInitialValues,
  step: 1,
  stepProgress: 0,
  premiums: [],
  premium: {
    tenure: 0,
    basePlan: 0,
    sgb: 0,
    hcb: 0,
    cs: 0,
    rped: 0,
    urc: 0,
    ncb: 0,
    mty: 0,
    total: 0,
    tax: 0,
    discount: 0
  },
  loadingSuggestion: false,
  suggestionError: '',
  loadingEstimate: false,
  suggestion: null,
  addOns: [],
  loadingAddOns: false,
  loadingPremium: false,
  premiumApiProgress: 0,
  policy: undefined,
  premiumFetched: false,
  finalizing: false,
  selectedAddOn: undefined,
  planWarning: undefined,
  addOnSelectionWarning: undefined,
  emiEnabled: false,
  deductiblesEnabled: false,
  //The below 6 fields are only required for migration flow
  previousPolicyNumber: '',
  previousPolicyDetails: null,
  loadingPreviousPolicyDetails: false,
  sumInsuredList: [],
  unavailableCoversList: [],
  migrationEmailId: '',
  validatingPincode: false,
  pincodeError: '',
  premiumApiError: '',
  // set combo and members
  planFormValid: false,
  setPlanFormValid: (isValid) => {
    set((state) => {
      state.planFormValid = isValid;
    });
  },
  setMemberNri: (index, value) => {
    set((state) => {
      const member = state.plan.members[index];
      if (member) {
        member.is_nri = value;
      }
    });
  },
  setCombo: (combo, maxAdults) => {
    const membersFinal =
      combo.name === ComboType.You
        ? maxAdults >= 1
          ? combo.members
          : []
        : maxAdults > 1
        ? combo.members
        : [];
    set((state) => {
      state.plan.members = membersFinal;
      state.policy = null;
    });
  },
  setAdults: (members) => {
    // extract the kids from current list
    const kids = get()
      .plan.members.filter(
        (item) =>
          item.relation === HealthQuoteMember.Son ||
          item.relation === HealthQuoteMember.Daughter
      )
      .slice();
    // new list of members is new set of member and kids
    set((state) => {
      state.plan.members = [...members, ...kids];
    });
  },
  // add member to plan
  addMember: (member, gender) => {
    set((state) => {
      state.plan.members.push({
        relation: member,
        ...(gender ? { gender } : {})
      });
      // Sort members after adding
      state.plan.members = sortMembersByRelation(state.plan.members);
    });
  },
  setMemberGender: (member, gender) => {
    set((state) => {
      const existingMember = state.plan.members.find(
        (item) => item.relation === member
      );
      if (existingMember) {
        existingMember.gender = gender;
      }
    });
  },
  resetDraftMembers: () => {
    set((state) => {
      state.plan.draftMembers = state.plan.members;
    });
  },
  setMemberDiseases: (index, diseases, syncAddOns = true) => {
    const addOns = get().addOns;
    set((state) => {
      const member = state.plan.members[index];
      if (member) {
        member.diseases = diseases;
        if (syncAddOns) {
          member.addons = member?.addons?.map((addOn) => {
            const planAddOn = addOns.find((item) => item.key === addOn.key);
            if (planAddOn && addOn.included && planAddOn.noDefaultOption) {
              return {
                ...addOn,
                sub_options: diseases.map((disease) => ({
                  key: 'med_conditions',
                  value: disease
                }))
              };
            }
            return addOn;
          });
        }
      }
    });
  },
  setDraftMemberDiseases: (index, diseases, syncAddOns = true) => {
    const addOns = get().addOns;
    set((state) => {
      const member = state.plan.draftMembers[index];
      if (member) {
        member.diseases = diseases;
        if (syncAddOns) {
          member.addons = member?.addons?.map((addOn) => {
            const planAddOn = addOns.find((item) => item.key === addOn.key);
            if (planAddOn && addOn.included && planAddOn.noDefaultOption) {
              return {
                ...addOn,
                sub_options: diseases.map((disease) => ({
                  key: 'med_conditions',
                  value: disease
                }))
              };
            }
            return addOn;
          });
        }
      }
    });
  },
  setDraftMemberAddOns: (index, addOns) => {
    set((state) => {
      const member = state.plan.draftMembers[index];
      if (member) {
        member.addons = addOns;
      }
    });
  },
  setMemberAddOns: (index, addOns) => {
    set((state) => {
      const member = state.plan.members[index];
      if (member) {
        member.addons = addOns;
      }
    });
  },
  // remove member from plan
  removeMember: (member) => {
    // if son or daughter remove the first one from the list
    if (member === HealthQuoteMember.Son || member === HealthQuoteMember.Daughter) {
      const members = get().plan.members.slice();
      const firstIndex = findIndex(members, (item) => item.relation === member);
      if (firstIndex !== undefined && firstIndex !== -1) {
        members.splice(firstIndex, 1);
        set((state) => {
          state.plan.members = sortMembersByRelation(members);
        });
      }
    } else {
      set((state) => {
        const filteredMembers = state.plan.members.filter(
          (item) => item.relation !== member
        );
        state.plan.members = sortMembersByRelation(filteredMembers);
      });
    }
  },
  /**
   * Update the plan details and call suggestion api to fetch the suggestion
   * @param members
   * @param pincode
   * @param ped
   */
  updatePlanDetails: async (members, pincode, ped) => {
    set((state) => {
      state.plan.members = members;
      state.plan.ped = ped;
      state.plan.pincode = pincode;
    });
    try {
      await get().getCoverSuggestion();
    } catch (err) {
      // handle any reset logic if we have any for this step
    }
  },
  validatePincode: async (fastPlan, formIsValid = true) => {
    const pincode = fastPlan.pincode;
    const plan = get().plan;
    const product = plan.product;
    const productDetails = getHealthProductDetails(plan.product);
    const boundaries = productDetails?.boundaries;
    set((state) => {
      state.validatingPincode = true;
      state.pincodeError = '';
      state.premiumApiProgress = 0;
    });
    try {
      if (fastPlan.pincode.toString().length === 6) {
        if (plan.product) {
          const resp = await insureAPIs.verifyHealthPincode({ pincode, product });
          const isValid = resp.data.data === 'Pincode is valid' ? true : false;
          if (isValid) {
            get().setFastPlanDetails(
              {
                ...fastPlan,
                members: getMembersWithAgeAndMedConditions(
                  plan.members,
                  fastPlan,
                  boundaries?.conditionRequired
                )
              },
              formIsValid
            );
          } else {
            set((state) => {
              state.pincodeError = 'Pincode not allowed by the insurer';
            });
            get().clearPremiums();
          }
        } else {
          const resp = await insureAPIs.validateHealthPincode({ pincode });
          const city = resp.data.city ? true : false;
          if (city) {
            get().setFastPlanDetails(
              {
                ...fastPlan,
                members: getMembersWithAgeAndMedConditions(
                  plan.members,
                  fastPlan,
                  boundaries?.conditionRequired
                )
              },
              formIsValid
            );
          } else {
            set((state) => {
              state.pincodeError = 'Pincode not allowed by the insurer';
            });
            get().clearPremiums();
          }
        }
      } else {
        set((state) => {
          state.pincodeError = 'Invalid Pincode';
        });
      }
    } catch (err) {
      // handle any reset logic if we have any for this step
      set((state) => {
        state.pincodeError = 'Invalid Pincode';
        state.premiumFetched = false;
      });
    } finally {
      set((state) => {
        state.validatingPincode = false;
      });
    }
  },
  /**
   * Update the plan details and call suggestion api to fetch the suggestion
   * @param members
   * @param pincode
   * @param ped
   */
  setFastPlanDetails: async (fastPlan, formIsValid = true) => {
    const plan = get().plan;
    const {
      members,
      pincode,
      ped,
      sumInsured,
      deductibleAmount,
      planType,
      porting,
      policy_start_date,
      loyalty_discount,
      existing_policy_number
    } = fastPlan;

    let updatedMembers = members;

    //For ACTIV_ONE and ACTIV_FIT since the riders are different for different plan type, we reset the addon selection on input value changes.
    if (
      (plan.product === HEALTH_INSURANCE_PRODUCTS.ACTIV_ONE ||
        plan.product === HEALTH_INSURANCE_PRODUCTS.ACTIV_FIT) &&
      (planType !== plan?.planType ||
        pincode !== plan?.pincode ||
        fastPlan?.SelfAge !==
          plan?.members?.find((item) => item?.relation === 'Self')?.age)
    ) {
      updatedMembers = members.map((item) => {
        return {
          ...item,
          opd_care: false,
          cc: false,
          diseases: []
        };
      });

      set((state) => {
        state.addOns = [];
        state.plan.members = updatedMembers;
      });
    }

    set((state) => {
      state.plan.members = updatedMembers;
      state.plan.ped = ped;
      state.plan.pincode = pincode;
      state.plan.sumInsured = sumInsured;
      state.plan.deductible = deductibleAmount;
      state.plan.planType = planType;
      state.plan.porting = porting;
      state.plan.policy_start_date = porting ? policy_start_date : null;
      state.plan.loyalty_discount = loyalty_discount;
      state.plan.existing_policy_number = existing_policy_number;
      state.policy = null;
      state.loadingPremium = true;
      state.premiumApiError = '';
    });
    try {
      // refetching boundaries if product is set
      if (plan.product) {
        await get().fetchProductBoundaries([plan.product]);
        /**
         * Fetch the features for the policy details page,
         * Keeping it here since this has to be re-fetched when plan values changes
         */
        await get().fetchProductFeatures([plan.product]);
      }
      // Check if the above boundaries are valid or not, if not fetch again with new sumInsured
      const boundariesInvalid = get().boundariesInvalid;
      if (boundariesInvalid) {
        await get().fetchProductBoundaries([plan.product]);
        // refetching features since boundaries have changed
        await get().fetchProductFeatures([plan.product]);
      }
      if (formIsValid) {
        await get().getPremium();
      }
    } catch (err) {
      // handle any reset logic if we have any for this step
    }
  },
  setSumInsured: async (sumInsured) => {
    const plan = get().plan;

    set((state) => {
      state.plan.sumInsured = sumInsured;
    });
    try {
      // refetching boundaries if sumInsured  is set
      if (plan.product) {
        await get().fetchProductBoundaries([plan.product]);
        await get().fetchProductFeatures([plan.product]);
      }
    } catch (err) {
      // handle any reset logic if we have any for this step
    }
  },
  /**
   * clear suggestion
   */
  clearSuggestion: () => {
    set((state) => {
      state.suggestion = null;
    });
  },
  /**
   * Update the user's sum insured value trigger estimate API
   * @param latestSumInsured
   */
  updateUserSumInsured: async (latestSumInsured) => {
    const prevSumInsured = get().plan.sumInsured;
    set((state) => {
      state.plan.sumInsured = latestSumInsured;
      state.addOns = [];
    });
    try {
      // get latest estimate since the sumInsured has changed
      await get().getEstimate();
    } catch (err) {
      // reset state since the api has failed
      set((state) => {
        state.plan.sumInsured = prevSumInsured;
      });
    }
  },
  getCoverSuggestion: async () => {
    const plan = get().plan;
    const quote = get().quote;

    set((state) => {
      // clear existing suggestion
      state.suggestion = null;
      state.loadingSuggestion = true;
      // Reset addOns whenever suggestion changes in order to refetch addons later
      state.addOns = [];
      // Reset error message if any
      state.suggestionError = '';
    });

    try {
      const suggestionResponse = await insureAPIs.getHealthSuggestions(
        healthSuggestionTransform(plan, quote)
      );
      const { suggestedCover, allCovers, premium } = suggestionResponse.data;
      const suggestion: Suggestion = {
        recommendedInsurer: premium.recommendedInsurer,
        allCovers,
        suggestedCover,
        products: premium.products
      };
      set((state) => {
        // reset loading state
        state.loadingSuggestion = false;
        // set suggestion in store
        state.suggestion = suggestion;
        // update users cover to recommended value if not already set
        state.plan = {
          ...plan,
          sumInsured: suggestion.suggestedCover
        };
        // update the premium if product is already set
        state.premium = getHealthPremium(plan.product, suggestion);
      });

      /**
       * After receiving the suggestion fetch boundaries for all products in the suggestion
       * The boundaries contain data points which powers the components from this point onwards
       */
      const productKeys = suggestion.products
        .filter((product) => {
          return product.success;
        })
        .map((product) => product.key);
      if (productKeys.length) {
        get().fetchProductBoundaries(productKeys);
      }
    } catch (err) {
      set((state) => {
        state.loadingSuggestion = false;
        state.suggestionError =
          'An error occurred while retrieving recommended cover! Please check again later.';
      });
      // rethrow error so that any store functions using getSuggestion can reset the state
      throw err;
    }
  },
  getEstimate: async () => {
    const plan = get().plan;
    const suggestion = get().suggestion;
    const quote = get().quote;
    const productsList = get().productsList;

    set((state) => {
      state.loadingEstimate = true;
      // Reset addOns whenever estimate changes in order to refetch addons later
      state.addOns = [];
    });

    try {
      const premium = await insureAPIs.getHealthEstimate(
        healthEstimateTransform(plan, quote)
      );

      const { products, recommendedInsurer } = premium.data;
      // Modify existing suggestion with latest values
      const newSuggestion: Suggestion = {
        ...suggestion!,
        products,
        recommendedInsurer
      };
      // Get premium from latest suggestion
      const premiumObj = getHealthPremium(
        plan.product
          ? plan.product
          : newSuggestion.products.find(
              (item) => item.insurerKey === recommendedInsurer && item.recommended
            )?.key,
        newSuggestion
      );

      // check if all products in response is present in store
      const missingProducts = products.filter(
        (product: SuggestedHealthProduct) =>
          product.success &&
          !productsList.find(
            (item) => item.product && item.product.key === product.key
          )
      );

      // if there are missing products get boundaries for available products
      if (missingProducts.length) {
        await get().fetchProductBoundaries(
          products
            .filter((item: SuggestedHealthProduct) => item.success)
            .map((item: SuggestedHealthProduct) => item.key)
        );
      }

      set((state) => {
        if (state.suggestion) {
          state.suggestion = newSuggestion;
        }
        state.loadingEstimate = false;
        state.premium = premiumObj;
      });
    } catch (err) {
      set((state) => {
        state.loadingEstimate = false;
      });
      // rethrow error so that any store functions using getEstimate can reset the state
      throw err;
    }
  },
  // set the current step
  setStep: (latestStep: number) => {
    const prevStep = get().step;
    const product = get().plan.product;
    const quote = get().quote;
    set((state) => {
      state.step = latestStep;
      state.stepProgress = getStepProgress(latestStep, get().quote);
    });

    // reset the plan when user navigates to members page
    if (
      (quote === QuoteType.Fast && latestStep === HEALTH_FAST_QUOTE_STEPS.MEMBERS) ||
      (quote === QuoteType.Normal && latestStep === HEALTH_QUOTE_STEPS.MEMBERS)
    ) {
      get().resetHealthPlan();
    } else if (
      latestStep === HEALTH_QUOTE_STEPS.POLICY &&
      prevStep > HEALTH_QUOTE_STEPS.POLICY
    ) {
      // reset the user product to refetch the boundaries and clear premiums
      get().setUserProduct(product);
    }
  },
  setUserProduct: (latestProduct: string) => {
    const suggestion = get().suggestion;
    /**
     * whenever the selected product changes, replace
     * the premium object with the premium of the selected product
     */
    const premiumObj = getHealthPremium(latestProduct, suggestion ?? undefined);

    set((state) => {
      state.plan.product = latestProduct;
      state.premium = { ...premiumObj };
      // reset addOns, premiums when product changes
      state.addOns = [];
      state.premiums = [];
      state.plan.tenure = 1;
      state.premiumApiError = '';
    });
    // fetch the product features when user selects a valid product
    if (latestProduct !== '') {
      get().fetchProductFeatures([latestProduct]);
    }
  },
  // set the product and quote type as fast
  setFastQuote: async (
    latestProduct,
    emiEnabled = false,
    deductiblesEnabled = false
  ) => {
    const prevProduct = get().plan.product;
    const productDetails = getHealthProductDetails(latestProduct);
    set((state) => {
      state.quote = QuoteType.Fast;
      state.plan.product = latestProduct;
      state.emiEnabled = emiEnabled;
      state.deductiblesEnabled = deductiblesEnabled;
      state.premiumApiError = '';
    });
    // Fetch the product boundaries for the fast quote product
    if (latestProduct && latestProduct !== prevProduct && !productDetails) {
      await get().fetchProductBoundaries([latestProduct]);
      await get().fetchProductFeatures([latestProduct]);
    }
  },
  getPremium: async () => {
    const plan = get().plan;
    const addOns = get().addOns;
    const emiEnabled = get().emiEnabled;
    const productDetails = getHealthProductDetails(plan.product);
    const allCovers = productDetails?.boundaries.allCovers;
    const unavailableCovers = productDetails?.boundaries.unavailableCovers;
    const boundaries = productDetails?.boundaries;
    const prevPremium = get().premium;

    const availableCovers =
      allCovers && unavailableCovers
        ? allCovers.filter((cover) => !unavailableCovers.includes(cover))
        : [];

    // fetch premiums for the next 2 covers above the selected sumInsured.
    // 'unlimited' ranks as the highest cover, so it only gets requested when it
    // genuinely falls within those next covers (not blindly appended).
    const otherCovers: SumInsuredValue[] =
      typeof plan.sumInsured === 'number'
        ? availableCovers
            .filter(
              (cover) =>
                isUnlimitedSI(cover) ||
                (typeof cover === 'number' && cover > (plan.sumInsured as number))
            )
            .sort((a, b) => {
              if (isUnlimitedSI(a)) return 1;
              if (isUnlimitedSI(b)) return -1;
              return a - b;
            })
            .slice(0, 2)
        : [];

    const previousPolicyNumber = get().previousPolicyNumber;

    set((state) => {
      state.loadingPremium = true;
      state.premiumApiProgress = 0;
      state.premiumApiError = '';
    });

    let error = '';
    // For migration we need to use a different API.
    // If previousPolicyNumber has value it means the flow is migration
    try {
      const resp = previousPolicyNumber
        ? await insureAPIs.getRenewalPremium(
            healthPremiumMigrationTransform(
              get().previousPolicyNumber,
              get().plan.sumInsured as number,
              addOns,
              OPTIMA_RESTORE,
              OPTIMA_SECURE,
              plan.planType || ''
            )
          )
        : await insureAPIs.getHealthPremium(
            healthPremiumTransform(
              plan,
              addOns,
              emiEnabled,
              otherCovers,
              boundaries
            ),
            // Track upload progress
            (progressEvent: AxiosProgressEvent) => {
              set((state) => {
                // For upload, we'll use 0-50% of our total progress
                const uploadPercentage = Math.round(
                  (progressEvent.loaded * 50) / (progressEvent.total ?? 100)
                );
                state.premiumApiProgress = uploadPercentage;
              });
            },
            // Add download progress tracking
            (progressEvent: AxiosProgressEvent) => {
              set((state) => {
                // For download, we'll use 50-100% of our total progress
                const downloadPercentage =
                  50 +
                  Math.round(
                    (progressEvent.loaded * 50) / (progressEvent.total ?? 100)
                  );
                state.premiumApiProgress = downloadPercentage;
              });
            }
          );

      const premiumsList: HealthPremiumResponse = !previousPolicyNumber
        ? (resp.data as HealthPremiumResponse)
        : [
            {
              sumInsured: plan.sumInsured,
              premiums: 'premiums' in resp.data ? resp.data.premiums : [],
              addOns: 'addOns' in resp.data ? resp.data.addOns : [],
              boundaries: undefined
            }
          ];

      // primary refers to the premiums and addOns for the selected sumInsured
      const primaryPremiums = premiumsList.find(
        (item) => item.sumInsured === plan.sumInsured
      );

      const premiums = primaryPremiums?.premiums ?? [];
      const primaryAddons = primaryPremiums?.addOns ?? [];

      // check if response has a premium with current tenure selected by user
      const tenureItem = premiums.find(
        (item) => item.success && item.tenure === plan?.tenure
      );

      // if tenureItem is found use that as tenure otherwise use the first success tenure
      const tenure = tenureItem
        ? tenureItem.tenure
        : premiums.find((item) => item.success)?.tenure ?? 1;

      // find premium for the tenure selected
      const tenurePremium: HealthPremium | undefined = premiums.find(
        (item) => item.tenure === tenure && item.success
      );

      let addOnsWithPremium: HealthAddOn[] = [];

      // modify,store addons only if one valid premium is found
      if (tenurePremium) {
        /**
         * Modify the current addons list with latest premium if addOns are already
         * present in store otherwise store the addOns in state with premium
         */
        if (addOns.length) {
          addOnsWithPremium = addOns.map((addOn) => {
            const addOnInResponse = primaryAddons.find(
              (aon) => aon.key === addOn.key
            );

            return {
              ...addOn,
              ...addOnInResponse,
              additionalPremium: tenurePremium
                ? tenurePremium[addOn.key as keyof HealthAddOnPremiums]
                : 0,
              included:
                // For mandatory add-ons without canSelectAlternatively, always keep them included
                // For mandatory add-ons with canSelectAlternatively, respect user's selection
                addOnInResponse?.mandatory &&
                !addOnInResponse?.canSelectAlternatively?.length
                  ? true
                  : // If the add-on is no longer eligible for the new tenure (or was
                  // dropped from the response entirely), force-deselect it so it can't
                  // be silently sent to the BE while hidden from the UI.
                  addOnInResponse?.isEligible ?? false
                  ? addOn.included
                  : false,
              subOptionValues:
                addOnInResponse?.subOptions && addOnInResponse?.subOptions.length
                  ? {
                      ...addOnInResponse.subOptions.reduce(
                        (prev, subOption) => ({
                          [subOption.key]: subOption.values[0],
                          ...prev
                        }),
                        {}
                      ),
                      ...addOn.subOptionValues
                    }
                  : undefined
            };
          });

          // Handle mutually exclusive mandatory addons: ensure at least one is selected
          addOnsWithPremium.forEach((addOn) => {
            if (
              addOn.mandatory &&
              addOn.canSelectAlternatively &&
              addOn.canSelectAlternatively.length > 0
            ) {
              // Check if none of the mutually exclusive addons are selected
              const groupKeys = [addOn.key, ...addOn.canSelectAlternatively];
              const anySelected = addOnsWithPremium.some(
                (ao) => groupKeys.includes(ao.key) && ao.included
              );

              // If none are selected, select the first one in the group
              if (!anySelected) {
                const firstInGroup = addOnsWithPremium.find(
                  (ao) => ao.key === groupKeys[0]
                );
                if (firstInGroup) {
                  firstInGroup.included = true;
                }
              }
            }
          });
        } else {
          addOnsWithPremium = primaryAddons.map((addOn) => {
            return {
              ...addOn,
              included: addOn.mandatory,
              additionalPremium: tenurePremium
                ? tenurePremium[addOn.key as keyof HealthAddOnPremiums]
                : 0,
              subOptionValues:
                addOn.subOptions && addOn.subOptions.length
                  ? addOn.subOptions.reduce(
                      (prev, subOption) => ({
                        ...prev,
                        [subOption.key]: subOption.values[0]
                      }),
                      {}
                    )
                  : undefined
            };
          });
        }
      } else {
        error = 'No valid premium found';
      }

      const latestProductList = [...get().productsList];

      // update the productsList with latest boundaries from premium response
      if (isEmpty(error)) {
        // Update existing productList with latest values if response contains boundaries
        if (primaryPremiums?.boundaries) {
          const current = latestProductList.findIndex(
            (item) => item.product.key === plan.product
          );
          if (current !== -1) {
            latestProductList[current] = {
              ...latestProductList[current],
              boundaries: {
                ...latestProductList[current].boundaries,
                ...primaryPremiums?.boundaries
              }
            };
          }
        }
      }

      set((state) => {
        state.premiumFetched = !!tenurePremium;
        state.loadingPremium = false;
        state.premiums = premiumsList;
        state.plan.tenure = tenure;
        state.premium = tenurePremium;
        state.addOns = addOnsWithPremium;
        state.productsList = latestProductList;
        state.premiumApiError = error;
      });
    } catch (err) {
      // reset states if needed
      set((state) => {
        state.loadingPremium = false;
        state.premium = prevPremium;
      });
      throw err;
    }
  },
  toggleAddOn: async (
    addOnKey: string,
    included: boolean,
    options?: {
      subOptionKey: string;
      value: HealthAddOnSubOptionValue;
    }
  ) => {
    const currentAddOns = get().addOns;
    let isModified = false;
    let planWarning = '';

    const addOnsToDeselect: string[] = [];
    const addOnsToSelect: string[] = [];
    const conflictingAddOns: HealthAddOns = [];

    const currentAddOn = currentAddOns.find((ao) => ao.key === addOnKey);

    if (included) {
      // Find conflicting add-ons that need to be deselected
      currentAddOns.forEach((addon) => {
        if (
          addon.included &&
          addon.key !== addOnKey &&
          addon.canSelectAlternatively?.includes(addOnKey)
        ) {
          addOnsToDeselect.push(addon.key);
          conflictingAddOns.push(addon);
        }
      });
    } else {
      // Find alternatives to auto-select when deselecting
      currentAddOn?.canSelectAlternatively?.forEach((altKey) => {
        const altAddOn = currentAddOns.find((ao) => ao.key === altKey);
        if (
          altAddOn &&
          !altAddOn.included &&
          (currentAddOn.mandatory || altAddOn.mandatory)
        ) {
          addOnsToSelect.push(altKey);
          conflictingAddOns.push(altAddOn);
        }
      });
    }

    if (conflictingAddOns.length) {
      // Set the new addOnSelectionWarning for the modal
      const addons: string[] = [];
      if (included && currentAddOn?.name) {
        // When selecting: From = conflicting addon, To = new addon
        addons.push(...conflictingAddOns.map((ao) => ao.name));
        addons.push(currentAddOn.name);
      } else {
        // When deselecting: From = current addon, To = auto-selected addon
        if (currentAddOn?.name) {
          addons.push(currentAddOn.name);
        }
        addons.push(...conflictingAddOns.map((ao) => ao.name));
      }

      set((state) => {
        state.addOnSelectionWarning = {
          addons,
          description: 'Because only one of these addons is allowed to be selected'
        };
      });
    }

    const modified = currentAddOns.map((ao) => {
      if (ao.key === addOnKey) {
        // if add on is mandatory don't allow it be removed, unless it has canSelectAlternatively
        if (ao.mandatory && !included && !ao.canSelectAlternatively?.length) {
          planWarning = ao.mandatoryWarning;
        } else if (
          ao.canNotSelectAlongWith &&
          ao.canNotSelectAlongWith.length &&
          included
        ) {
          const addOnsPresent: HealthAddOns = [];
          ao.canNotSelectAlongWith.forEach((key) => {
            const matchedAddon = currentAddOns.find(
              (item) => item.key === key && item.included
            );
            if (matchedAddon) {
              addOnsPresent.push(matchedAddon);
            }
          });
          if (addOnsPresent.length) {
            planWarning =
              addOnsPresent.length === 1
                ? `Selection of both ${ao.name} and ${addOnsPresent[0].name} not allowed.`
                : `Selection of ${ao.name} along with ${addOnsPresent.map(
                    (pao) => pao.name
                  )} not allowed.`;
          } else {
            // addons are modified in this case
            isModified = true;
            return {
              ...ao,
              included,
              subOptionValues: {
                ...ao.subOptionValues,
                ...(options ? { [options.subOptionKey]: options?.value } : {})
              }
            };
          }
        } else {
          // addons are modified in this case
          isModified = true;
          return {
            ...ao,
            included,
            subOptionValues: {
              ...ao.subOptionValues,
              ...(options ? { [options.subOptionKey]: options?.value } : {})
            }
          };
        }
      }
      // Deselect add-ons in the canSelectAlternatively group
      if (addOnsToDeselect.includes(ao.key)) {
        isModified = true;
        return { ...ao, included: false };
      }
      // Auto-select add-ons when deselecting from a group
      if (addOnsToSelect.includes(ao.key)) {
        isModified = true;
        return { ...ao, included: true };
      }
      return { ...ao };
    });
    // whenever an addon is added or removed, update the state and refetch the premium
    if (isModified) {
      set((state) => {
        state.addOns = modified;
      });
      try {
        await get().getPremium();
      } catch (err) {
        // Reset addOns to previous state if premium api fails
        set((state) => {
          state.addOns = currentAddOns;
        });
      }
    } else {
      // set the plan warning to state
      set((state) => {
        state.planWarning = planWarning;
      });
    }
  },
  toggleModalAddOn: async (
    addOnKey: string,
    members: HealthMember[],
    included = true
  ) => {
    const currentAddOns = get().addOns;

    let isModified = false;
    const modified = currentAddOns.map((ao) => {
      if (ao.key === addOnKey) {
        isModified = true;
        return {
          ...ao,
          included
        };
      }
      return { ...ao };
    });

    // whenever an addon is added or removed, update the state and refetch the premium
    if (isModified) {
      set((state) => {
        state.addOns = modified;
      });
    }

    // update the members with the addon key, if addon is removed, remove the addon from members
    const updatedMembers = included
      ? members
      : members.map((item) => {
          const filteredAddOns =
            item.addons?.filter((addon) => addon.key !== addOnKey) || [];

          // Check if there are any remaining noDefaultOption addons that are included
          const noDefaultOptionAddOnsIncluded = modified
            .filter(
              (addOn) =>
                addOn.noDefaultOption && addOn.included && addOn.key !== addOnKey
            )
            .map((addOn) => addOn.key);

          // Reset diseases if no noDefaultOption addons remain
          const shouldResetDiseases = !filteredAddOns.some((addOn) =>
            noDefaultOptionAddOnsIncluded.includes(addOn.key)
          );

          return {
            ...item,
            [addOnKey]: false,
            addons: filteredAddOns,
            ...(shouldResetDiseases && { diseases: [] })
          };
        });

    set((state) => {
      state.plan = {
        ...state.plan,
        members: updatedMembers,
        draftMembers: updatedMembers
      };
    });

    try {
      await get().getPremium();
    } catch (err) {
      set((state) => {
        //On error we revert back to the previous addons
        state.addOns = currentAddOns;
      });
    }
  },

  finalizeHealthQuote: async (user: RegUser) => {
    try {
      set((state) => {
        state.finalizing = true;
      });

      const plan = get().plan;
      const addOns = get().addOns;
      const productDetails = getHealthProductDetails(plan.product);
      const boundaries = productDetails?.boundaries;

      const response = await insureAPIs.finalizeHealthQuote(
        finalizeHealthQuoteTransform(plan, addOns, user, boundaries)
      );

      const policyId = response.data.policy_id;
      if (policyId) {
        set((state) => {
          state.plan.mobile = user.mobile ?? '';
          state.policy = response.data;
          state.finalizing = false;
        });
        // On quote finalize and policyId creation, remove team from session storage
        // Not necessary to check for existence before removal,
        // if policyID is created, the flow is complete and we remove team since it is saved in db (if visited via team link)
        SessionStorage?.remove('team');
      } else {
        throw new Error('Policy ID not received from server');
      }
    } catch (error: any) {
      console.error('Error finalizing health quote:', error);
      set((state) => {
        state.finalizing = false;
      });

      // Re-throw the error so it can be handled by the calling component
      throw error;
    }
  },
  resetHealthPlan: (clearAll = false) => {
    const quote = get().quote;
    const plan = get().plan;
    const product = plan.product;
    // omitting addons from members while resetting plan as addons will be reset
    const members = plan.members.map((member) => omit(member, ['addons']));

    const quoteInitialValues =
      quote === QuoteType.Fast
        ? { ...planInitialValues, product }
        : planInitialValues;

    set((state) => {
      // keeping members and combo intact
      if (!clearAll) {
        state.plan = {
          ...quoteInitialValues,
          members,
          sumInsured: plan.sumInsured
        };
      } else {
        state.plan = quoteInitialValues;
      }
      state.addOns = [];
      state.suggestion = null;
      state.premium = undefined;
      state.premiums = [];
      state.premiumFetched = false;
    });
  },
  toggleEmi: (enabled) => {
    const premium = get().premium;
    // if plan has emiFrequency set use that otherwise set monthly or the first valid frequency
    const frequency =
      premium?.emi && Object.keys(premium?.emi).length
        ? premium.emi.Monthly
          ? HealthEMIFrequency.Monthly
          : (Object.keys(premium?.emi)[0] as HealthEMIFrequency)
        : undefined;
    set((state) => {
      state.plan.optedEmi = enabled;
      if (enabled) {
        state.plan.emiFrequency = frequency;
      }
    });
  },
  updateEmiFrequency: (emiOption) => {
    set((state) => {
      state.plan.emiFrequency = emiOption;
    });
  },
  updateTenure: async (tenure) => {
    const product = get().plan.product;

    set((state) => {
      state.plan.tenure = tenure;
    });

    // Care Ultimate has tenure-dependent addon eligibility — re-fetch to get fresh isEligible per addon.
    // Other products don't change addon eligibility on tenure switch, so recompute locally.
    if (product === HEALTH_INSURANCE_PRODUCTS.CARE_ULTIMATE) {
      await get().getPremium();
      return;
    }

    const premiumsList = get().premiums;
    const premiums = premiumsList.find(
      (item) => item.sumInsured === get().plan.sumInsured
    )?.premiums;
    const addOns = get().addOns;
    const latestPremium = premiums?.find((item) => item.tenure === tenure);

    const addOnsWithPremium = addOns.map((addOn) => ({
      ...addOn,
      additionalPremium: latestPremium
        ? latestPremium[addOn.key as keyof HealthAddOnPremiums]
        : 0
    }));

    set((state) => {
      state.premium = latestPremium;
      state.addOns = addOnsWithPremium;
    });
  },
  toggleAddOnDetail: (addOnKey) => {
    set((state) => {
      state.selectedAddOn = addOnKey;
    });
  },
  clearPlanWarning: () => {
    set((state) => {
      state.planWarning = undefined;
    });
  },
  clearAddOnSelectionWarning: () => {
    set((state) => {
      state.addOnSelectionWarning = undefined;
    });
  },
  clearPremiums: (keepFetchedInfo = false) => {
    const addOns = get().addOns;
    const members = get().plan.members;
    const updatedMembers = members.map((member: any) => {
      const updatedMember = { ...member, addons: [] };
      addOns.forEach((addOn) => {
        updatedMember[addOn.key] = false;
      });
      return updatedMember;
    });

    set((state) => {
      state.premiums = [];
      state.premium = undefined;
      state.addOns = [];
      state.plan.members = updatedMembers;
      if (!keepFetchedInfo) state.premiumFetched = false;
    });
  },
  resetProduct: () => {
    set((state) => {
      state.plan.product = '';
    });
  },
  setPreviousPolicyNumber: async (previousPolicyNumber: string) => {
    set((state) => {
      state.previousPolicyNumber = previousPolicyNumber;
      state.loadingPreviousPolicyDetails = true;
    });

    const payload = {
      policy_number: previousPolicyNumber,
      product: OPTIMA_RESTORE
    };

    try {
      const resp = await insureAPIs.getPolicyInformationForHealthMigration(payload);

      if (resp?.data) {
        //Possible cover amount values
        //Should be greater than or equal to existing cover amount
        const sumInsuredList = resp?.data?.available_covers[OPTIMA_SECURE].filter(
          (item: number) => item >= resp.data.sum_insured
        );

        const unavailableCoversList = resp?.data?.unavailable_covers[
          OPTIMA_SECURE
        ].filter((item: number) => item >= resp.data.sum_insured);

        set((state) => {
          state.sumInsuredList = sumInsuredList;
          state.unavailableCoversList = unavailableCoversList;
          state.plan.sumInsured = sumInsuredList[0];
          state.previousPolicyDetails = resp?.data;
          state.plan.planType = resp?.data?.plan_type || '';
        });

        get().getPremium();
      }
    } catch (error) {
      // handle error logic if any
    } finally {
      //reset loading state
      set((state) => {
        state.loadingPreviousPolicyDetails = false;
      });
    }
  },

  setSumInsuredForMigration: (sumInsured: SumInsuredValue) => {
    set((state) => {
      state.plan.sumInsured = sumInsured;
    });

    get().getPremium();
  },

  finalizeMigrationHealthQuote: async (user: RegUserMigration) => {
    const plan = get().plan;
    const addOns = get().addOns;

    const oldDetails = {
      old_product: OPTIMA_RESTORE,
      old_sum_insured: get().previousPolicyDetails?.sum_insured,
      old_tenure: get().previousPolicyDetails?.tenure,
      policy_number: get().previousPolicyNumber
    };

    try {
      const response = await insureAPIs.finalizeMigrationHealthQuote(
        finalizeMigrationHealthQuoteTransform(plan, addOns, user, oldDetails)
      );

      const policyId = response.data.policy_id;
      if (policyId) {
        set((state) => {
          state.plan.mobile = user.mobile ?? '';
          state.policy = response.data;
        });
      }
    } catch (err) {
      //Handle error logic
    }
  },

  setMigrationEmailId: (emailId: string) => {
    set((state) => {
      state.migrationEmailId = emailId;
    });
  },
  clearPolicy: () => {
    set((state) => {
      state.policy = undefined;
    });
  }
});
